import { DropdownOption, FileUploadListItem, FileUploadType } from '@aslanpay/portal-common-lib';

// FileUploadStatus is sourced locally (app/types/fileProcessingService) because it carries
// PARTIALLY_SUCCEEDED, which the SDK's enum does not yet include.
import { FileUploadStatus } from 'app/types/fileProcessingService';

export type { FileUploadListItem, FileUploadType };
export type { FileUploadStatus };

// The SDK's FileUploadListItem is slightly behind the backend on the uploader name fields, so
// augment them here. They are optional and safe to read off the API response.
export interface FileUploadRow extends FileUploadListItem {
  uploadedByFirstName?: string;
  uploadedByLastName?: string;
}

// fps upload/analysis statuses that represent a failure. bulkImportStatus is stored loosely typed
// (see ImportEmployeesData) because it carries both the legacy company-manager BulkImportStatus
// values and these real fps ones — used to detect the error state on the confirm screen and to
// know when a back-navigation should cancel the upload instead of just going back a step.
export const FAILED_UPLOAD_STATUSES: FileUploadStatus[] = ['FAILED', 'VALIDATION_FAILED'];

// GIFTING_ONLY_EMPLOYEE_DATA_FILE exists in fps but isn't in the published FileUploadType enum yet
// (see ASD-3739) — cast it structurally so the filter list works correctly against the real API.
const GIFTING_ONLY_EMPLOYEE_DATA_FILE = 'GIFTING_ONLY_EMPLOYEE_DATA_FILE' as FileUploadType;

// Payroll upload types the backoffice supports for now (EDF / gifting EDF / AET / ELF).
export const SUPPORTED_FILE_UPLOAD_TYPES: FileUploadType[] = [
  'EMPLOYEE_DATA_FILE',
  GIFTING_ONLY_EMPLOYEE_DATA_FILE,
  'APPROVED_EARNINGS_TIMESHEET',
  'EMPLOYEE_LEAVERS_FILE',
];

export const FILE_UPLOAD_TYPE_LABELS: Partial<Record<FileUploadType, string>> = {
  EMPLOYEE_DATA_FILE: 'Employee data',
  [GIFTING_ONLY_EMPLOYEE_DATA_FILE]: 'Employee data (gifting)',
  APPROVED_EARNINGS_TIMESHEET: 'Timesheet',
  EMPLOYEE_LEAVERS_FILE: 'Leavers',
  SAVINGS_PRESET_IMAGE: 'Savings preset image',
};

// The savings preset cover-image catalogue is global (no owning company). It is deliberately kept
// out of SUPPORTED_FILE_UPLOAD_TYPES above, which drives the company-scoped payroll file filter.
export const SAVINGS_PRESET_IMAGE_UPLOAD_TYPE: FileUploadType = 'SAVINGS_PRESET_IMAGE';

export const FILE_UPLOAD_STATUS_LABELS: Record<FileUploadStatus, string> = {
  UPLOAD_REQUESTED: 'Uploading',
  ANALYSIS_IN_PROGRESS: 'Analysing',
  AWAITING_CLIENT_CONFIRMATION: 'Awaiting approval',
  CLIENT_CONFIRMED: 'Confirmed',
  VALIDATION_IN_PROGRESS: 'Validating',
  VALIDATED: 'Validated',
  PROCESSING_IN_PROGRESS: 'Processing',
  SUCCEEDED: 'Succeeded',
  PARTIALLY_SUCCEEDED: 'Partially succeeded',
  FAILED: 'Failed',
  VALIDATION_FAILED: 'Validation failed',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
};

// Chakra Badge colorScheme per status.
export const FILE_UPLOAD_STATUS_COLOR: Record<FileUploadStatus, string> = {
  SUCCEEDED: 'green',
  PARTIALLY_SUCCEEDED: 'yellow',
  VALIDATED: 'green',
  CLIENT_CONFIRMED: 'green',
  UPLOAD_REQUESTED: 'blue',
  ANALYSIS_IN_PROGRESS: 'blue',
  AWAITING_CLIENT_CONFIRMATION: 'blue',
  VALIDATION_IN_PROGRESS: 'blue',
  PROCESSING_IN_PROGRESS: 'blue',
  FAILED: 'red',
  VALIDATION_FAILED: 'red',
  CANCELLED: 'orange',
  EXPIRED: 'orange',
};

export const FILE_UPLOAD_TYPE_OPTIONS: DropdownOption[] = SUPPORTED_FILE_UPLOAD_TYPES.map(
  (value) => ({ value, text: FILE_UPLOAD_TYPE_LABELS[value] ?? value })
);

export const FILE_UPLOAD_STATUS_OPTIONS: DropdownOption[] = (
  Object.keys(FILE_UPLOAD_STATUS_LABELS) as FileUploadStatus[]
).map((value) => ({ value, text: FILE_UPLOAD_STATUS_LABELS[value] }));
